import { module } from 'constant'
import { useState, useEvent } from 'hooks'
import { Jodit } from 'jodit'
import $ from 'jquery'
import 'jodit/esm/plugins/all'
import { getWysiwygEditorSelectors } from 'selectors'
import { cn } from 'utils'

type State = {
	wysiwyg?: HTMLElement
	editor?: HTMLElement
	wrapper?: HTMLElement
	textBlock?: HTMLDivElement
	container?: HTMLElement
	content?: HTMLDivElement
	iconClasses: Array<string>
	jodit?: Jodit
	saveButton: HTMLButtonElement
}

const customButtons = ['icons', 'translations', 'date']

const { selector } = module.creator.wysiwygEditor

export const WysiwygEditor = () => {
	const { state, setState } = useState<State>()

	const initWysiwygEditor = (isAjax?: boolean) => {
		const { editor } = state
		if (!editor) {
			return
		}

		const { jodit } = state
		if (isAjax && typeof jodit !== 'undefined') {
			jodit.destruct()
			createWysiwyg(editor)
		} else {
			createWysiwyg(editor)
		}
	}

	const createWysiwyg = async (editor: HTMLElement) => {
		const instance = Jodit.make(editor, {
			language: 'en',
			cleanHTML: {
				removeEmptyElements: false,
			},
			enter: 'br',
			toolbarButtonSize: 'small',
			showCharsCounter: false,
			toolbarSticky: false,
			showWordsCounter: false,
			showXPathInStatusbar: false,
			buttons: [...Jodit.defaultOptions.buttons, ...customButtons],
			controls: getCustomButtons(),
			toolbarAdaptive: false,
			uploader: {
				insertImageAsBase64URI: true,
			},
			removeButtons: ['about', 'ai-assistant', 'ai-commands', 'speechRecognize'],
			iframe: true,
			popupRoot: document.body,
		})

		setState({ ...getWysiwygEditorSelectors(), jodit: instance })
		loadContent(editor, instance)
		const iconClasses = await fetchIcons()
		setState({ iconClasses })
	}

	const getCustomButtons = (): object => ({
		...getDateButtonControl(),
		...getTranslationButtonControl(),
		...getIconsButtonControl(),
	})

	const getIconsButtonControl = (): object => ({
		icons: {
			name: 'icons',
			tooltip: 'icons',
			tags: ['icons'],
			popup: (editor: Jodit) => {
				const selectionInfo = editor.s.save()
				if (!selectionInfo) {
					return null
				}
				return getIcons(editor)
			},
		},
	})

	const getTranslationButtonControl = (): object => ({
		translations: {
			name: 'translations',
			exec: (editor: Jodit) => {
				editor.selection.insertHTML("{_'replace-translation-key'}")
			},
		},
	})

	const getDateButtonControl = (): object => ({
		date: {
			name: 'date',
			exec: (editor: Jodit) => {
				editor.selection.insertHTML("{$date|date:'%d.%m.%Y'}")
			},
		},
	})

	const getIcons = (jodit: Jodit) => {
		const { iconClasses } = state
		if (!iconClasses) {
			return null
		}

		const fragment = document.createDocumentFragment()
		const container = document.createElement('div')
		cn.addClass(container, 'jodit-popup-container__icons')
		for (let i = 0; i < iconClasses.length; i++) {
			const iconClass = iconClasses[i]
			const icon = document.createElement('i')
			icon.setAttribute('class', `fas ${iconClass}`)
			container.appendChild(icon)

			handleClickIcon(icon, jodit)
		}

		fragment.appendChild(container)
		return fragment
	}

	const handleClickIcon = (el: HTMLElement, jodit: Jodit) => {
		const click = useEvent(el, 'click')
		click.register(() => {
			jodit.s.restore()
			jodit.s.insertNode(el)
			jodit.e.fire('closeAllPopups')
		})
	}

	const fetchIcons = async () => {
		const { editor } = state
		if (!editor) {
			return null
		}

		const link = editor.getAttribute(selector.iconsHandler)
		if (!link) {
			return null
		}

		const payload = await window.fetch(link).then((res) => res.json())
		return Object.values(payload.icons)
	}

	const loadContent = (el: HTMLElement, instance: Jodit) => {
		const defaultContent = el.getAttribute(selector.value)

		if (defaultContent !== null && defaultContent !== '') {
			instance.value = defaultContent
			return
		}

		const link = el.getAttribute(selector.loadHandler)
		const { content } = state
		if (!link || !content) {
			return
		}

		window
			.fetch(link)
			.then((res) => res.json())
			.then((payload) => {
				content.innerHTML = payload.html
			})
	}

	const initSaveContent = () => {
		const { content, editor, saveButton } = state
		if (!content || !editor || !saveButton) {
			return
		}

		const saveLink = saveButton.getAttribute(selector.saveHandler)
		if (!saveLink) {
			return
		}

		const click = useEvent(saveButton, 'click')
		click.register(() => {
			const contentHtml = content.innerHTML
			if (!contentHtml) {
				return
			}

			handleAjax(saveLink, { text: contentHtml })
		})
	}

	const handleAjax = (link: string, data: object) => {
		// @ts-ignore
		$.nette.ajax({
			method: 'POST',
			url: link,
			data,
		})
	}

	const init = (isAjax?: boolean) => {
		setState({ ...getWysiwygEditorSelectors() })
		initWysiwygEditor(isAjax)
		initSaveContent()
	}

	return {
		init,
	}
}
